import { useQueries } from "@tanstack/react-query";
import { getTagResourceCounts } from "@/services/tagsApi";

export interface TagsDeviceCountResult {
  /** Sum of `devices` counts across the requested tag ids. When more than one
   *  tag is selected this is an upper bound (overlapping memberships are
   *  counted once per tag) — the consumer should label it as such. */
  total: number;
  /** True while at least one underlying tag count is still loading. */
  isLoading: boolean;
  /** True if any underlying request failed. The fallback is to silently hide
   *  the badge — never block save. */
  hasError: boolean;
  /** Convenience flag for the multi-tag "upper bound" wording. */
  isExact: boolean;
  /**
   * True when every requested tag's count was served by react-query's
   * cache without firing a network request this render — i.e., none of
   * the queries are loading or background-fetching, and every query
   * already has data. Lets the UI flag "Using cached counts" so users
   * understand the number wasn't recomputed against the live API.
   *
   * Always `false` when `tagIds` is empty (nothing to cache) and while
   * any query is still loading or revalidating. Backward-compatible:
   * existing callers that ignore this field keep working unchanged.
   */
  fromCache: boolean;
}

/**
 * Aggregates the device count for an arbitrary set of tag ids using the
 * existing `/api/v1/tags/{id}/resources/count` endpoint.
 *
 * - Empty input returns zero, never fires a request.
 * - Single tag: `total` is exact (`isExact: true`).
 * - Multiple tags: `total` is the sum and represents an *upper bound* because
 *   the API doesn't union device sets across tags.
 */
export function useTagsDeviceCount(tagIds: string[]): TagsDeviceCountResult {
  const queries = useQueries({
    queries: tagIds.map((id) => ({
      queryKey: ["tag-resource-counts", id],
      queryFn: () => getTagResourceCounts(id),
      // Tag membership counts are slow-moving and reused across every
      // wizard surface (App Config, PPPC, Add App). Keep them fresh for
      // 10 minutes and retain unused entries in the cache for an hour so
      // switching between wizards — or stepping back into the same one —
      // never re-fires a network request for tags we already counted.
      staleTime: 10 * 60_000,
      gcTime: 60 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    })),
  });

  const total = queries.reduce((sum, q) => {
    const data = q.data as any;
    if (!data) return sum;
    const n =
      data.devices ?? data.device_count ?? data.deviceCount ?? 0;
    return sum + (typeof n === "number" ? n : 0);
  }, 0);

  // "fromCache" is the strongest signal we can derive from react-query
  // without bypassing its public API: every query already has data
  // (cache hit), nothing is loading, and nothing is currently
  // revalidating in the background. We deliberately treat in-flight
  // background refetches as "not from cache" so the badge disappears
  // the moment a fresh number is on its way — the user shouldn't see a
  // "cached" tag while we're in the act of replacing it.
  const fromCache =
    tagIds.length > 0 &&
    queries.length > 0 &&
    queries.every((q) => !q.isLoading && !q.isFetching && q.data !== undefined);

  return {
    total,
    isLoading: queries.some((q) => q.isLoading),
    hasError: queries.some((q) => q.isError),
    isExact: tagIds.length <= 1,
    fromCache,
  };
}
